import React, { useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { SettingsContext } from '@/context/settings-context'
import { useAdContent, usePageData, useAdAssets, useAISuggestions } from '../hooks'
import { MultiCopyInput } from './adContent/MultiCopyInput'
import { AdPreview } from '@/components/ui/ad-preview'
import { CTADropdown } from './adContent/CTADropdown'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Recommendations } from './adContent/Recommendations'
import { WinningCopyChips } from './adContent/WinningCopyChips'
import { useWinningCopy, withCopy } from '../hooks/useWinningCopy'
import { CubeLoader } from '@/components/ui/cube-loader'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { GET_AD_CREATIVE_ASSET_VARATIONS_BY_AD_CREATIVE_ID } from '@/graphql/ads/ad-assets-queries'
import { useCarouselCards } from '@/components/mediaBuying/adCreatives/screens/carouselCards/hooks/useCarouselCards.js'
import _ from 'lodash'

// Constants
const AD_CONTENT_DEFAULTS = {
    primaryText: [''],
    headline: [''],
    description: [''],
    cta: 'LEARN_MORE',
    destinationUrl: '',
    displayLink: '',
}

const CHARACTER_LIMITS = {
    headline: 255,
    description: 255,
}

const RECOMMENDATION_LIMITS = {
    headline: 40,
    description: 25,
    primaryText: 40,
}

const MAX_VARIATIONS = {
    primaryText: 5,
    headline: 5,
    description: 5,
}

export const SetAdContent = () => {
    const [assetVariations, setAssetVariations] = useState([])
    const [assetVariationsLoading, setAssetVariationsLoading] = useState(true)
    const { id: campaignId } = useParams()
    const {
        getStepContentData,
        setStepContentData,
        registerCustomNavigation,
        clearCustomNavigation,
        goToStep,
        goBack,
        dataForStepper,
        id: creativeTempId,
    } = useWizard()
    const { connectedPublishers } = useMediaBuyingContext()
    const { theme } = useContext(SettingsContext)

    const selectFormatData = getStepContentData('selectFormat') || {}
    const setMediaCropData = getStepContentData('setMediaCrop') || {}
    // const mediaLibraryData = getStepContentData('mediaLibrary') || {}
    const showInstantForm = selectFormatData.showLeadForm || false
    const adFormat = selectFormatData.format || 'singleImage'
    const leadFormData = getStepContentData('leadForm') || {}

    // Initialize form with cached data
    const cachedData = getStepContentData('setAdContent')
    const initialFormData = cachedData
        ? {
              primaryText: cachedData.primaryText || AD_CONTENT_DEFAULTS.primaryText,
              headline: cachedData.headline || AD_CONTENT_DEFAULTS.headline,
              description: cachedData.description || AD_CONTENT_DEFAULTS.description,
              cta: cachedData.cta || AD_CONTENT_DEFAULTS.cta,
              destinationUrl: cachedData.destinationUrl || AD_CONTENT_DEFAULTS.destinationUrl,
              displayLink: cachedData.displayLink || AD_CONTENT_DEFAULTS.displayLink,
          }
        : AD_CONTENT_DEFAULTS

    // Use custom hooks
    const { formData, updateField, updateFields, validate, errors } = useAdContent(initialFormData)
    const { copies: winningCopies, automated: winningCopyAutomated } = useWinningCopy(campaignId)
    // A creative that has no saved content yet is new: only then may the winning copy be filled in for the user
    const isNewContent = useRef(!cachedData)
    const copyEdited = useRef(false)
    const [prefilled, setPrefilled] = useState(false)
    const destinationUrlRef = useRef(null)
    const { pageTitle, pageIcon } = usePageData(connectedPublishers)

    useQuery(GET_AD_CREATIVE_ASSET_VARATIONS_BY_AD_CREATIVE_ID, {
        variables: { adCreativeId: creativeTempId },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const variations = data.AdCreativeAssetVariation.getByAdCreativeId
            setAssetVariations(variations)
            setAssetVariationsLoading(false)
        },
    })

    // Try to get assetId from multiple sources
    const { assetData, refetchAssets } = useAdAssets(undefined, undefined, setMediaCropData?.assetId)
    const { cards, refetchAssets: refetchCarousel } = useCarouselCards(campaignId, creativeTempId)
    const { aiSuggestions, loading: aiLoading } = useAISuggestions(campaignId)

    // Enhance assetData with trim data from variations
    const enhancedAssetData = React.useMemo(() => {
        if (!assetData || !assetVariations.length) return assetData

        // Find trim data from any variation (they should all have the same trim data)
        const variationWithTrim = assetVariations.find((v) => v.adCreativeAsset?.trim_data)
        const rawTrimData = variationWithTrim?.adCreativeAsset?.trim_data

        // Convert milliseconds to seconds (same as MediaCrop screen)
        const trimData = rawTrimData
            ? {
                  start: rawTrimData.start ? rawTrimData.start / 1000 : null,
                  end: rawTrimData.end ? rawTrimData.end / 1000 : null,
              }
            : null

        return {
            ...assetData,
            trim_data: trimData || assetData.trim_data,
        }
    }, [assetData, assetVariations])

    // Enhance carousel cards with trim data
    const enhancedCards = React.useMemo(() => {
        if (!cards || !assetVariations.length) return cards

        return cards.map((card) => {
            // Find variation for this card's asset
            const cardVariation = assetVariations.find(
                (v) => v.ad_creative_asset_id === card.id || v.adCreativeAsset?.id === card.id,
            )

            const rawTrimData = cardVariation?.adCreativeAsset?.trim_data

            // Convert milliseconds to seconds (same as MediaCrop screen)
            const trimData = rawTrimData
                ? {
                      start: rawTrimData.start ? rawTrimData.start / 1000 : null,
                      end: rawTrimData.end ? rawTrimData.end / 1000 : null,
                  }
                : null

            return trimData
                ? {
                      ...card,
                      trim_data: trimData,
                  }
                : card
        })
    }, [cards, assetVariations])

    // Force component update when assetId becomes available
    const [, forceUpdate] = React.useReducer((x) => x + 1, 0)

    useEffect(() => {
        document.body.classList.remove('light', 'dark')
        document.body.classList.add(theme)
    }, [theme])

    // Watch for assetId changes and force update
    useEffect(() => {
        if (setMediaCropData?.assetId) {
            // Force a re-render when assetId becomes available
            forceUpdate()
            refetchAssets()
        }
    }, [setMediaCropData?.assetId])

    // Automated mode: a new creative whose headline and primary text are still empty starts from the best winning copy
    useEffect(() => {
        if (!isNewContent.current || copyEdited.current || prefilled) return
        if (!winningCopyAutomated || winningCopies.length === 0) return
        const empty = (variations) => (variations || []).every((value) => !String(value || '').trim())
        if (!empty(formData.primaryText) || !empty(formData.headline)) return
        const [best] = winningCopies
        const updates = {}
        if (best.primaryText) updates.primaryText = [best.primaryText]
        if (best.headline && selectFormatData?.format !== 'carousel') updates.headline = [best.headline]
        if (Object.keys(updates).length === 0) return
        updateFields(updates)
        setPrefilled(true)
    }, [
        winningCopies,
        winningCopyAutomated,
        formData.primaryText,
        formData.headline,
        prefilled,
        selectFormatData?.format,
        updateFields,
    ])

    const handleCopyChange = (field) => (value) => {
        copyEdited.current = true
        setPrefilled(false)
        updateField(field, value)
    }

    // Picking a chip fills the first empty variation or adds one; typed text is never replaced
    const handlePickWinningCopy = (copy) => {
        const primaryText = withCopy(formData.primaryText, copy.primaryText, MAX_VARIATIONS.primaryText)
        const headline =
            selectFormatData?.format === 'carousel'
                ? null
                : withCopy(formData.headline, copy.headline, MAX_VARIATIONS.headline)
        if (!primaryText && !headline) return
        copyEdited.current = true
        setPrefilled(false)
        updateFields({ ...(primaryText && { primaryText }), ...(headline && { headline }) })
    }

    // Refetch carousel assets when component mounts to ensure fresh crop data
    useEffect(() => {
        if (adFormat === 'carousel') refetchCarousel()
    }, [adFormat, refetchCarousel])

    // Handle custom navigation with validation - save form data explicitly before navigating to avoid race with unmount
    const handleNextNavigation = useCallback(() => {
        const isValid = validate(!_.isEmpty(leadFormData))
        if (isValid) {
            setStepContentData(formData, 'setAdContent')
            goToStep('reviewSave')
        } else {
            destinationUrlRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            destinationUrlRef.current?.focus()
        }
    }, [validate, leadFormData, goToStep, formData, setStepContentData])

    // Register custom navigation
    useEffect(() => {
        const CustomNav = () => {
            return (
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                        Back
                    </Button>
                    <Button onClick={handleNextNavigation} className="tw:w-[156px] tw:!text-sm">
                        Next
                    </Button>
                </div>
            )
        }

        registerCustomNavigation(() => <CustomNav />)

        return () => clearCustomNavigation()
        // }, [currentStep, goBack, handleNextNavigation, registerCustomNavigation, clearCustomNavigation])
    }, [showInstantForm, formData])

    if (assetVariationsLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="setAdContent" />

            <div className="flex-grow-1 p-4 overflow-auto">
                <div className="tw:flex tw:h-full tw:gap-6">
                    {/* Left Panel - Form */}
                    <div className="tw:flex-1 tw:max-w-[60%] tw:overflow-auto tw:pr-4">
                        <WinningCopyChips copies={winningCopies} onPick={handlePickWinningCopy} />
                        {prefilled && (
                            <p className="tw:mb-3 tw:text-xs tw:text-gray-200" role="status">
                                Filled from what worked; edit freely
                            </p>
                        )}

                        {/* Primary Text */}
                        <MultiCopyInput
                            label="Primary Text"
                            variations={formData.primaryText}
                            onChange={handleCopyChange('primaryText')}
                            maxVariations={MAX_VARIATIONS.primaryText}
                            placeholder="Your ad’s primary text goes here. Make it engaging!"
                            isTextarea={false}
                            aiSuggestions={aiSuggestions.subheaders}
                            aiLoading={aiLoading}
                            recommendationLimit={RECOMMENDATION_LIMITS.primaryText}
                            showAIOnFocus={true}
                            tooltip="The primary text for your ad appears in most placements, though the position varies"
                        />

                        {/* Headline */}
                        {selectFormatData?.format !== 'carousel' && (
                            <MultiCopyInput
                                label="Headline"
                                variations={formData.headline}
                                onChange={handleCopyChange('headline')}
                                maxVariations={MAX_VARIATIONS.headline}
                                characterLimit={CHARACTER_LIMITS.headline}
                                placeholder="Catchy headline"
                                isTextarea={false}
                                aiSuggestions={aiSuggestions.headers}
                                aiLoading={aiLoading}
                                recommendationLimit={RECOMMENDATION_LIMITS.headline}
                                showAIOnFocus={true}
                                tooltip="Add up to 5 brief headlines to let people know what your ad is about. Each headline can have a maximum of 255 characters. Headlines won't appear in all placements."
                            />
                        )}

                        {/* Description */}
                        {selectFormatData?.format !== 'carousel' && (
                            <MultiCopyInput
                                label="Description"
                                variations={formData.description}
                                onChange={(value) => updateField('description', value)}
                                maxVariations={MAX_VARIATIONS.description}
                                characterLimit={CHARACTER_LIMITS.description}
                                placeholder="Additional description"
                                isTextarea={false}
                                aiSuggestions={aiSuggestions.descriptions}
                                aiLoading={aiLoading}
                                recommendationLimit={RECOMMENDATION_LIMITS.description}
                                showAIOnFocus={true}
                                tooltip="Add optional descriptive text that provides additional details about your offer. This text may not appear in all ad placements."
                            />
                        )}

                        {/* Call to Action */}
                        <CTADropdown
                            value={formData.cta}
                            onChange={(value) => updateField('cta', value)}
                            tooltip="Show a button or link on your ad that represents the action you want people to take."
                            leadFormMode={showInstantForm}
                        />

                        {/* URL Fields - Hidden if instant form is attached */}
                        {!showInstantForm && (
                            <>
                                <div className="tw:mb-6">
                                    <label className="tw:text-sm tw:font-semibold tw:text-white tw:mb-1.5 tw:block">
                                        Destination URL
                                    </label>
                                    <Input
                                        ref={destinationUrlRef}
                                        type="url"
                                        value={formData.destinationUrl}
                                        onChange={(e) =>
                                            updateField(
                                                'destinationUrl',
                                                String(e.target.value || '').replace(/^https?:\/\//, ''),
                                            )
                                        }
                                        placeholder="Ex. https://yourwebsite.com/landing-page"
                                        className="tw:h-[40px]"
                                        aria-invalid={!!errors?.destinationUrl}
                                    />
                                    {errors?.destinationUrl && (
                                        <p className="tw:text-xs tw:text-destructive tw:mt-1.5">
                                            {errors.destinationUrl}
                                        </p>
                                    )}
                                </div>

                                <div className="tw:mb-6">
                                    <label className="tw:text-sm tw:font-semibold tw:text-white tw:mb-1.5 tw:block">
                                        Display Link
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.displayLink}
                                        onChange={(e) => updateField('displayLink', e.target.value)}
                                        placeholder="Ex. yourwebsite.com"
                                        className="tw:h-[40px]"
                                    />
                                </div>
                            </>
                        )}

                        <Recommendations />
                    </div>

                    {/* Right Panel - Preview */}
                    <div className="tw:w-[40%] border-left-white-1 ps-4 tw:h-[230px]">
                        <AdPreview
                            primaryText={formData.primaryText}
                            headline={formData.headline}
                            description={formData.description}
                            cta={formData.cta}
                            pageTitle={pageTitle}
                            pageIcon={pageIcon}
                            assetData={enhancedAssetData}
                            carouselAssets={adFormat === 'carousel' ? enhancedCards : []}
                            assetVariations={assetVariations}
                            format={'square'}
                            showInstantForm={showInstantForm}
                            displayLink={formData.displayLink}
                            destinationUrl={formData.destinationUrl}
                            showDefaultPrimaryText={false}
                            adCreativeFormat={adFormat}
                            cropStateFromMediaCrop={setMediaCropData?.cropState}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
